import {
  AsYouType,
  getCountries,
  getCountryCallingCode,
  parsePhoneNumberFromString,
  type CountryCode,
} from "libphonenumber-js"
import { z } from "zod"

export const DEFAULT_PHONE_COUNTRY: CountryCode = "BR"

/** Bandeira emoji a partir do ISO-2 (regional indicator offset). */
export function countryToEmoji(iso2: string): string {
  const cc = iso2.toUpperCase()
  if (cc.length !== 2) return ""
  const A = 0x1f1e6
  const base = "A".charCodeAt(0)
  return String.fromCodePoint(
    A + (cc.charCodeAt(0) - base),
    A + (cc.charCodeAt(1) - base)
  )
}

/**
 * Normaliza qualquer entrada para E.164 (`+5511999998888`) ou `null`.
 * Tolerante ao legado BR cru (dígitos sem DDI): assume `defaultCountry`.
 */
export function toE164(
  input: string | null | undefined,
  defaultCountry: CountryCode = DEFAULT_PHONE_COUNTRY
): string | null {
  if (!input) return null
  const trimmed = input.trim()
  if (!trimmed) return null
  const parsed = parsePhoneNumberFromString(
    trimmed,
    trimmed.startsWith("+") ? undefined : defaultCountry
  )
  if (!parsed || !parsed.isValid()) return null
  return parsed.number
}

export function isValidPhone(
  input: string | null | undefined,
  defaultCountry: CountryCode = DEFAULT_PHONE_COUNTRY
): boolean {
  return toE164(input, defaultCountry) !== null
}

/** Número nacional formatado p/ exibição a partir de E.164 (ou cru). */
export function formatNational(
  input: string | null | undefined,
  defaultCountry: CountryCode = DEFAULT_PHONE_COUNTRY
): string {
  if (!input) return ""
  const parsed = parsePhoneNumberFromString(
    input.trim(),
    input.trim().startsWith("+") ? undefined : defaultCountry
  )
  return parsed ? parsed.formatNational() : input
}

/** Formatação as-you-type do número nacional para o país dado. */
export function formatAsYouType(
  national: string,
  country: CountryCode
): string {
  return new AsYouType(country).input(national)
}

export interface PhoneCountry {
  iso2: CountryCode
  callingCode: string
  emoji: string
  name: string
}

/** Detecta país + número nacional a partir de um E.164 (ou legado). */
export function parsePhone(
  input: string | null | undefined,
  defaultCountry: CountryCode = DEFAULT_PHONE_COUNTRY
): { country: CountryCode; national: string } {
  if (input) {
    const parsed = parsePhoneNumberFromString(
      input.trim(),
      input.trim().startsWith("+") ? undefined : defaultCountry
    )
    if (parsed) {
      return {
        country: parsed.country ?? defaultCountry,
        national: parsed.nationalNumber,
      }
    }
  }
  return { country: defaultCountry, national: "" }
}

let cachedCountries: PhoneCountry[] | null = null

/** Lista completa de países (ISO-2, DDI, emoji, nome pt-BR), ordenada por nome. */
export function getPhoneCountries(): PhoneCountry[] {
  if (cachedCountries) return cachedCountries
  const regionNames =
    typeof Intl !== "undefined" && "DisplayNames" in Intl
      ? new Intl.DisplayNames(["pt-BR"], { type: "region" })
      : null
  cachedCountries = getCountries()
    .map((iso2) => ({
      iso2,
      callingCode: getCountryCallingCode(iso2),
      emoji: countryToEmoji(iso2),
      name: regionNames?.of(iso2) ?? iso2,
    }))
    .sort((a, b) => a.name.localeCompare(b.name, "pt-BR"))
  return cachedCountries
}

/**
 * Schema zod: valida e normaliza para E.164. Use `.optional()` para campos
 * opcionais (o cliente deve enviar `undefined` quando vazio).
 */
export const zPhone = z
  .string()
  .trim()
  .transform((v, ctx) => {
    const e164 = toE164(v)
    if (!e164) {
      ctx.addIssue({ code: "custom", message: "Telefone inválido." })
      return z.NEVER
    }
    return e164
  })
